/**
 * An `<img>` from the archive that waits its turn.
 *
 * Two rules, and one shared queue behind both of them:
 *
 * 1. **Nothing is fetched until it is nearly on screen.** `loading="lazy"` is still on
 *    the element, but it is advice rather than a rule — the browser decides, and the
 *    browser this was found on decided to fetch all hundred at once. An
 *    IntersectionObserver is a promise about *our* behaviour instead of a request for the
 *    browser's cooperation.
 * 2. **Never more than a few at a time**, across every picture on the page. See
 *    lib/loadGate.js for what went wrong without that, in one sentence: a hundred `<img>`
 *    elements committed in one render is a hundred simultaneous requests, which is what a
 *    scrape looks like to Cloudflare however comfortably it fits inside the archive's own
 *    ceilings.
 *
 * One gate, shared. The faces on Partner Units and the covers on the newsletter shelf
 * are the same browser asking the same Worker for pictures, and a ceiling each would be
 * two ceilings and no answer to somebody with both pages open.
 *
 * Callers pass a URL. What is on the other end of it — a media file, a PDF cover — is
 * not this component's business; `MediaImage` in ImageUpload.jsx is the wrapper that
 * knows about media rows and anonymised copies.
 *
 * The element keeps its box while it waits, which every caller's CSS provides with an
 * `aspect-ratio` — so the layout is the same before and after, there is nothing to
 * reflow, and the observer has a rectangle to work with. An `<img>` with no `src`
 * attribute at all is what waiting looks like: no request, and no broken-image glyph
 * either.
 */
import { useEffect, useRef, useState } from 'react'
import { createLoadGate } from '../lib/loadGate'

/*
 * How many archive pictures may be in flight at any one moment. Six is the old HTTP/1.1
 * per-host connection limit, which is a pace the whole web was built around and which no
 * page here notices: a screenful is a handful of pictures, so on arrival they all start
 * at once and the rest follows as the reader scrolls into it.
 */
const IMAGE_GATE = createLoadGate(6)

/*
 * How far outside the viewport a picture is fetched. Generous — most of a screen — so
 * that scrolling shows pictures rather than the boxes where pictures are about to be,
 * while a hundred cards still means only the dozen near the reader are asked for.
 */
const LOOKAHEAD = '600px 0px'

/* Read once. Every browser this application supports has it; the check is for the test
   runner and for anything else rendering these outside a browser. */
const CAN_OBSERVE = typeof IntersectionObserver === 'function'

/** The waiting and the queueing, without the element. */
function useGatedSrc(node, src, eager) {
  // A browser with no observer has no way to tell, and a picture that never loads is
  // worse than one fetched early — so there it starts out wanted, still behind the gate.
  const [near, setNear] = useState(eager || !CAN_OBSERVE)
  const [shown, setShown] = useState(null)
  const release = useRef(null)

  useEffect(() => {
    if (near) return undefined
    // No element means no picture — a caller renders nothing without an id or a URL, and
    // this hook still runs. There is nothing to watch and nothing to fetch.
    const el = node.current
    if (!el) return undefined
    /* A box of no size is a box the observer can never report as visible, which would be
       a picture that never appears. It means the CSS sizes this image by its own content
       rather than in the sheet, the way `.media-full` does, and the honest answer is to
       fetch it — behind the gate, like everything else. The callers where that is the
       normal state pass `eager` and never reach this line; this is here so that a new one
       that forgets fails by loading early rather than by showing nothing. */
    const box = el.getBoundingClientRect()
    if (!box.width || !box.height) {
      // In the effect because it is a reading of the layout, and there is no layout to
      // read until the element is on the page. It happens once, for one picture.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNear(true)
      return undefined
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true)
      },
      { rootMargin: LOOKAHEAD }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [near, node])

  /* The slot is taken here and given back by the element's own load or error, so it is
     held for exactly as long as the request is outstanding. This cleanup covers the two
     ways that can never arrive: the card scrolled out of a list that then unmounted, and
     a `src` that changed under a picture still loading. */
  useEffect(() => {
    if (!near || !src) return undefined
    let live = true
    const done = IMAGE_GATE.request(() => {
      // Synchronously, when a slot is already free, and that is the point rather than an
      // oversight: the pictures on screen when a page opens should start in this frame.
      if (live) setShown(src)
    })
    release.current = done
    return () => {
      live = false
      done()
      release.current = null
    }
  }, [near, src])

  const finished = () => {
    release.current?.()
    release.current = null
  }

  /* `shown` rather than `src` while a changed URL waits for its slot: the picture already
     on screen is the right thing to keep looking at until the new one is allowed to
     start, and it costs no request to go on showing it. */
  return [shown ?? undefined, finished]
}

/**
 * @param {string|null} src Where the picture is. Null renders nothing.
 * @param {boolean} eager Fetch it now. For a picture somebody has explicitly opened —
 *   one request, wanted at once, and not part of any wall of thumbnails.
 */
export function GatedImage({ src, alt, className, style, eager = false, draggable }) {
  // The element is the thing the observer watches, so the ref is made here and lent to
  // the hook rather than handed back by it.
  const node = useRef(null)
  const [gatedSrc, finished] = useGatedSrc(node, src, eager)
  return src ? (
    <img
      ref={node}
      className={className}
      style={style}
      src={gatedSrc}
      alt={alt || ''}
      draggable={draggable}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onLoad={finished}
      onError={finished}
    />
  ) : null
}
